import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Download,
  Printer,
  ExternalLink,
  Share2,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { apiClient } from '@/api/axios';
import { ShareOptionsModal } from '../shared/ShareOptionsModal';

interface DocumentInfo {
  name: string;
  date?: string;
  source?: string;
  type?: string;
  url?: string;
}

interface DocumentViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload?: (doc: DocumentInfo) => void;
  document: DocumentInfo;
}

export function DocumentViewModal({
  isOpen,
  onClose,
  onDownload,
  document,
}: DocumentViewModalProps) {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !document.url) {
      setPreviewUrl(null);
      setContentType('');
      setError(null);
      setLoading(false);
      return;
    }

    let mounted = true;

    // Revoke previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    setLoading(true);
    setPreviewUrl(null);
    setError(null);

    const load = async () => {
      try {
        let blob: Blob;
        let ct: string;

        if (
          document.url!.startsWith('http://') ||
          document.url!.startsWith('https://')
        ) {
          // Full URL — fetch directly as blob
          const res = await fetch(document.url!);
          if (!res.ok)
            throw new Error(`Failed to fetch document (${res.status})`);
          blob = await res.blob();
          ct =
            res.headers.get('content-type') ||
            guessContentType(document.url!, document.type);
        } else {
          // R2 key — fetch through our API which streams raw binary
          // cf-back /documents/:key/download returns the file bytes directly (not a JSON URL)
          const encodedKey = encodeURIComponent(document.url!);
          const res = await apiClient.get<Blob>(
            `/documents/${encodedKey}/download`,
            { params: { disposition: 'inline' }, responseType: 'blob' }
          );
          blob = res.data;
          ct =
            (res.headers as Record<string, string>)['content-type'] ||
            blob.type ||
            guessContentType(document.url!, document.type);
        }

        const blobUrl = URL.createObjectURL(blob);

        if (mounted) {
          blobUrlRef.current = blobUrl;
          setPreviewUrl(blobUrl);
          setContentType(ct);
        } else {
          URL.revokeObjectURL(blobUrl);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err instanceof Error ? err.message : 'Failed to load document'
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [isOpen, document.url, document.type]);

  // Revoke blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  if (!isOpen) return null;

  const isImage =
    /^image\//i.test(contentType) ||
    /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(previewUrl ?? '') ||
    /^(jpg|jpeg|png|gif|webp|svg)$/i.test(document.type ?? '');
  const isPdf =
    /pdf/i.test(contentType) ||
    /\.pdf(\?|$)/i.test(previewUrl ?? '') ||
    /^pdf$/i.test(document.type ?? '');

  const handlePrint = () => {
    if (previewUrl) {
      const win = window.open(previewUrl, '_blank');
      win?.addEventListener('load', () => win.print());
    }
  };

  const handleOpenNewTab = () => {
    if (previewUrl) window.open(previewUrl, '_blank');
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border dark:border-white/8 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b dark:border-white/8 shrink-0">
            <div>
              <h2 className="text-xl font-semibold dark:text-white">
                {document.name}
              </h2>
              {(document.date || document.source) && (
                <p className="text-sm text-gray-600 dark:text-white/70 mt-1">
                  {document.source && (
                    <span>
                      {t('epassportModal.uploadedByLabel', {
                        defaultValue: 'Uploaded by',
                      })}{' '}
                      <span className="font-medium">{document.source}</span>
                    </span>
                  )}
                  {document.date && (
                    <span className="ml-1 text-gray-400 dark:text-white/40">
                      · {new Date(document.date).toLocaleDateString()}
                    </span>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 dark:text-white" />
            </button>
          </div>

          {/* Document Preview */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-3 text-gray-500 dark:text-white/50">
                <Loader2 className="w-8 h-8 animate-spin text-[#936FEF]" />
                <p className="text-sm">Loading document…</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
                <AlertCircle className="w-10 h-10 text-red-400" />
                <p className="text-sm text-red-500 dark:text-red-400 text-center max-w-sm">
                  {error}
                </p>
              </div>
            ) : previewUrl && isImage ? (
              <div className="flex items-center justify-center bg-gray-100 dark:bg-[#0F0E16] rounded-lg overflow-hidden min-h-[300px]">
                <img
                  src={previewUrl}
                  alt={document.name}
                  className="max-w-full max-h-[55vh] object-contain rounded"
                />
              </div>
            ) : previewUrl && isPdf ? (
              <div
                className="bg-gray-100 dark:bg-[#0F0E16] rounded-lg overflow-hidden"
                style={{ height: '55vh' }}
              >
                <iframe
                  src={previewUrl}
                  title={document.name}
                  className="w-full h-full border-0"
                />
              </div>
            ) : (
              <div className="bg-gray-100 dark:bg-[#0F0E16] rounded-lg p-12 text-center border-2 border-dashed border-gray-300 dark:border-white/20">
                <div className="text-6xl mb-4">📄</div>
                <p className="text-gray-600 dark:text-white/70 mb-4">
                  {document.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-white/50">
                  {document.type || 'Document'} ·{' '}
                  {t('epassportModal.previewPlaceholder', {
                    defaultValue: 'Preview not available',
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between p-6 border-t dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16] shrink-0 flex-wrap gap-2">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => onDownload?.(document)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm transition-colors"
              >
                <Download className="w-4 h-4" />
                {t('epassportModal.download')}
              </button>
              <button
                onClick={() => setShareOpen(true)}
                disabled={!previewUrl}
                className="px-4 py-2 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg flex items-center gap-2 text-sm dark:text-white transition-colors disabled:opacity-40"
              >
                <Share2 className="w-4 h-4" />
                {t('common.share', { defaultValue: 'Share' })}
              </button>
              <button
                onClick={handlePrint}
                disabled={!previewUrl}
                className="px-4 py-2 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg flex items-center gap-2 text-sm dark:text-white transition-colors disabled:opacity-40"
              >
                <Printer className="w-4 h-4" />
                {t('epassportModal.print')}
              </button>
              <button
                onClick={handleOpenNewTab}
                disabled={!previewUrl}
                className="px-4 py-2 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg flex items-center gap-2 text-sm dark:text-white transition-colors disabled:opacity-40"
              >
                <ExternalLink className="w-4 h-4" />
                {t('epassportModal.openInNewTab')}
              </button>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg text-sm dark:text-white transition-colors"
            >
              {t('epassportModal.close')}
            </button>
          </div>
        </div>
      </div>

      <ShareOptionsModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title={document.name}
        message={`${t('epassportModal.documentPreview', { defaultValue: 'Document' })}: ${document.name}`}
        url={previewUrl ?? window.location.href}
        fileName={document.name}
        shareType="document"
      />
    </>
  );
}

function guessContentType(url: string, type?: string): string {
  if (type) {
    if (/^(jpg|jpeg|png|gif|webp|svg)$/i.test(type))
      return `image/${type.toLowerCase()}`;
    if (/^pdf$/i.test(type)) return 'application/pdf';
    if (type.includes('/')) return type;
  }
  if (/\.(jpg|jpeg)(\?|$)/i.test(url)) return 'image/jpeg';
  if (/\.png(\?|$)/i.test(url)) return 'image/png';
  if (/\.gif(\?|$)/i.test(url)) return 'image/gif';
  if (/\.webp(\?|$)/i.test(url)) return 'image/webp';
  if (/\.pdf(\?|$)/i.test(url)) return 'application/pdf';
  return '';
}
